import styled, { css } from 'styled-components';
import Image from '@components/image';
import Text from '@components/text';
import Section from '@components/section';
import { defaultTheme } from '@styles/theme.styled';

interface ContainerProps {
  enclosureColor?: keyof typeof defaultTheme.backgroundColors;
  orientation?: boolean;
}

export const StyledTextContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    text-align: center;
    max-width: 48rem;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 0.5rem;
      text-align: left;
    }
  `}
`;

export const Container = styled.div<ContainerProps>`
  ${({ theme, enclosureColor, orientation }) => css`
    display: flex;
    flex-direction: ${orientation ? 'row' : 'column'};
    gap: 3.5rem;
    align-items: ${orientation ? 'flex-start' : 'center'};
    width: 100%;
    padding: 6rem 4rem;
    background-color: ${enclosureColor
      ? theme.backgroundColors[enclosureColor]
      : theme.colors.secondary};
    border-radius: 2rem;

    ${StyledTextContent} {
      text-align: ${orientation ? 'left' : 'center'};

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        row-gap: 1.5rem;
        text-align: left;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      padding: 4.5rem 3rem;
      row-gap: 4rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 4rem 2.5rem;
      row-gap: 3.5rem;
      flex-direction: column;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: 3.5rem 2rem;
      row-gap: 3rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 3rem 1.5rem;
      align-items: flex-start;
      gap: 2rem;
    }
  `}
`;

interface StyledSectionProps {
  showOverflow: boolean;
}

export const StyledSection = styled(Section)<StyledSectionProps>`
  ${({ theme, showOverflow }) =>
    showOverflow
      ? css`
          padding-bottom: 0;

          &::after {
            content: '';
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 10rem;
            background-color: ${theme.colors.white};
          }
        `
      : css`
          padding-top: 5rem !important;

          @media only screen and (max-width: ${theme.breakpoints.xl}) {
            padding-top: 4rem !important;
          }

          @media only screen and (max-width: ${theme.breakpoints.lg}) {
            padding-top: 3.5rem !important;
          }

          @media only screen and (max-width: ${theme.breakpoints.md}) {
            padding-top: 3.25rem !important;
          }

          @media only screen and (max-width: ${theme.breakpoints.sm}) {
            padding-top: 3rem !important;
          }

          &::after {
            display: none;
          }
        `};
`;

export const StyledDescription = styled(Text)`
  opacity: 0.85;
`;

export const TextContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 0.5rem;
    }
  `}
`;

export const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`;

export const PaddingWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    padding: 0 3rem;
    max-width: 60rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: 0 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 0 1rem;
    }
  `}
`;

interface StyledGridProps {
  columns?: number;
}

export const StyledGrid = styled.div<StyledGridProps>`
  ${({ theme, columns }) => css`
    display: grid;
    gap: 3rem;
    width: 100%;
    grid-template-columns: repeat(${columns ?? 3}, 1fr);

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      grid-template-columns: repeat(2, 1fr);
      column-gap: 2rem;
      row-gap: 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 2rem;
      grid-template-columns: repeat(1, 1fr);
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 2rem;
    }
  `}
`;

export const StyledBlock = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
    }
  `}
`;

export const StyledIconImage = styled(Image)`
  width: 100%;
  height: 100%;
`;

export const StyledIcon = styled.div`
  ${({ theme }) => css`
    width: 100%;
    max-width: 3.5rem;
    height: 3.5rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      max-width: 3.25rem;
      height: 3.25rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      max-width: 3rem;
      height: 3rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      max-width: 3rem;
      height: 3rem;
    }
  `}
`;
